import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import CooperStatsView from './CooperStatsView';

import { useApiQuery } from '../hooks/useApiQuery';
import { useApiMutation } from '../hooks/useApiMutation';


const CooperStatsDashboard = () => {
  // Le hook useApiQuery gere : auto-retry 1x sur 502/503/504 + erreurs reseau,
  // safe JSON parse, distinction { type, status, detail } pour message contextuel.
  const { data: stats, error: queryError, loading, refetch: fetchStats } = useApiQuery('/api/coopers/stats/dashboard');
  const error = queryError ? queryError.detail : null;
  const [activeChart, setActiveChart] = useState('ca'); // 'ca' | 'dossiers' | 'membres'
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileForm, setProfileForm] = useState({
    nom: '', prenom: '', entreprise: '', telephone: '', territoire: 'Guadeloupe', siret: ''
  });
  const { mutate: submitProfile, isMutating: savingProfile } = useApiMutation('POST', '/api/coopers/self-profile', {
    successToast: 'Profil COOPER active avec succes',
    errorPrefix: 'Echec activation profil',
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-orange-200 bg-orange-50">
        <CardContent className="py-8 text-center">
          <AlertTriangle className="h-12 w-12 text-orange-500 mx-auto mb-3" />
          <p className="text-orange-700 font-medium">{error}</p>
          <button 
            onClick={fetchStats}
            className="mt-4 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            Réessayer
          </button>
        </CardContent>
      </Card>
    );
  }

  if (!stats || !stats.statistiques) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-gray-500">Aucune donnée disponible</p>
          <button 
            onClick={fetchStats}
            className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Actualiser
          </button>
        </CardContent>
      </Card>
    );
  }

  // Profil COOPER incomplet : afficher un message contextuel (pas une "erreur de connexion")
  if (stats.profil_incomplet) {
    return (
      <>
        <Card className="border-amber-200 bg-amber-50" data-testid="cooper-profile-incomplete">
          <CardContent className="py-10 text-center">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
            <p className="text-amber-900 font-semibold text-base mb-1">Profil COOPER en attente de configuration</p>
            <p className="text-amber-800 text-sm max-w-xl mx-auto">
              {stats.message || "Votre profil n'est pas encore relie a un compte COOPER actif. Vous pouvez completer vos informations vous-meme ci-dessous."}
            </p>
            <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
              <button
                onClick={() => setShowProfileModal(true)}
                className="px-5 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-semibold shadow-sm"
                data-testid="cooper-complete-profile-btn"
              >
                Completer mon profil COOPER
              </button>
              <button
                onClick={fetchStats}
                className="px-4 py-2 bg-white border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors text-sm"
              >
                <RefreshCw className="inline h-3.5 w-3.5 mr-1" />
                Actualiser
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Modal de saisie du profil */}
        {showProfileModal && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !savingProfile && setShowProfileModal(false)} data-testid="cooper-self-profile-modal">
            <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl border border-amber-200" onClick={(e) => e.stopPropagation()}>
              <div className="px-6 py-4 border-b border-amber-100 bg-amber-50 rounded-t-xl">
                <h3 className="text-lg font-bold text-amber-900">Completer mon profil COOPER</h3>
                <p className="text-xs text-amber-700 mt-0.5">Toutes les informations ci-dessous sont necessaires pour activer votre espace.</p>
              </div>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!profileForm.nom.trim() || !profileForm.prenom.trim()) {
                    toast.error('Nom et prenom sont obligatoires');
                    return;
                  }
                  try {
                    await submitProfile(profileForm);
                    setShowProfileModal(false);
                    fetchStats();
                  } catch {
                    /* toast d'erreur deja affiche par useApiMutation */
                  }
                }}
                className="p-6 space-y-3"
              >
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Prenom *</label>
                    <input type="text" value={profileForm.prenom} onChange={(e) => setProfileForm({ ...profileForm, prenom: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400" required data-testid="cooper-profile-prenom" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Nom *</label>
                    <input type="text" value={profileForm.nom} onChange={(e) => setProfileForm({ ...profileForm, nom: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400" required data-testid="cooper-profile-nom" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Structure / Entreprise</label>
                  <input type="text" value={profileForm.entreprise} onChange={(e) => setProfileForm({ ...profileForm, entreprise: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400" data-testid="cooper-profile-entreprise" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Telephone</label>
                    <input type="tel" value={profileForm.telephone} onChange={(e) => setProfileForm({ ...profileForm, telephone: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400" data-testid="cooper-profile-telephone" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Territoire</label>
                    <select value={profileForm.territoire} onChange={(e) => setProfileForm({ ...profileForm, territoire: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400" data-testid="cooper-profile-territoire">
                      <option value="Guadeloupe">Guadeloupe</option>
                      <option value="Martinique">Martinique</option>
                      <option value="Guyane">Guyane</option>
                      <option value="La Reunion">La Reunion</option>
                      <option value="Mayotte">Mayotte</option>
                      <option value="Saint-Martin">Saint-Martin</option>
                      <option value="Saint-Barthelemy">Saint-Barthelemy</option>
                      <option value="France metropolitaine">France metropolitaine</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">SIRET</label>
                  <input type="text" value={profileForm.siret} onChange={(e) => setProfileForm({ ...profileForm, siret: e.target.value.replace(/\D/g, '').slice(0, 14) })} maxLength={14} placeholder="14 chiffres" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400" data-testid="cooper-profile-siret" />
                </div>
                <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 mt-4">
                  <button type="button" onClick={() => setShowProfileModal(false)} disabled={savingProfile} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors">
                    Annuler
                  </button>
                  <button type="submit" disabled={savingProfile} className="px-5 py-2 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50" data-testid="cooper-profile-submit">
                    {savingProfile ? 'Enregistrement...' : 'Activer mon profil'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </>
    );
  }


  return (
    <CooperStatsView
      stats={stats}
      activeChart={activeChart}
      setActiveChart={setActiveChart}
      fetchStats={fetchStats}
    />
  );
};

export default CooperStatsDashboard;
